import { useEffect, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';
import { account, ApiError, type SecurityDto } from '../net/api';
import { Field } from './Field';
import { VerificationSection } from './Verification';

const errText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong.');
const when = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

/** Account panel: change email, and optional two-step verification with recovery codes. */
export function AccountSecurity({ supervised, isStaff }: { supervised?: boolean; isStaff?: boolean }) {
  const [s, setS] = useState<SecurityDto>();
  const load = () => account.security().then(setS, () => {});
  useEffect(() => {
    void load();
  }, []);
  if (!s) return null;
  return (
    <>
      {s.email && !supervised && <ChangeEmail s={s} />}
      {s.email && supervised && <p className="muted small-print">Only your parent can change your email. They can do it from their parent controls.</p>}
      <h3 className="mini-head">Two-step verification</h3>
      <TwoStep s={s} isStaff={isStaff} onChanged={() => void load()} />
      <VerificationSection email={s.email} />
    </>
  );
}

function ChangeEmail({ s }: { s: SecurityDto }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string; fields?: Record<string, string> }>();
  if (s.nextEmailChange) return <p className="muted small-print">You can change your email on {when(s.nextEmailChange)} (once every 90 days, and not in an account’s first 60 days).</p>;
  if (!open) return <button className="link-btn small-print" onClick={() => setOpen(true)}>Change my email</button>;
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      const r = await account.changeEmail(String(f.get('newEmail')), String(f.get('password')), String(f.get('code') ?? '') || undefined);
      setMsg({ kind: 'ok', text: `Almost done: open the link we sent to ${r.to} to confirm it. Your old address gets a note too.` });
    } catch (err) {
      setMsg({ kind: 'err', text: errText(err), fields: err instanceof ApiError ? err.fields : undefined });
    }
  }
  return (
    <form className="stack" onSubmit={submit} noValidate>
      <Field id="newEmail" name="newEmail" label="New email" type="email" required error={msg?.fields?.newEmail} />
      <Field id="email-password" name="password" label="Your password" type="password" autoComplete="current-password" required error={msg?.fields?.password} />
      {s.enabled && <Field id="email-code" name="code" label="Two-step code" autoComplete="one-time-code" error={msg?.fields?.code} />}
      <p className="muted small-print">You can change your email once every 90 days. We’ll send a confirm link to the new address, and a “this wasn’t me” link to the old one that works for 7 days.</p>
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      <div className="row-wrap">
        <button className="btn small">Send confirm link</button>
        <button type="button" className="btn small ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

function TwoStep({ s, isStaff, onChanged }: { s: SecurityDto; isStaff?: boolean; onChanged: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; qr: string }>();
  const [codes, setCodes] = useState<string[]>();
  const [off, setOff] = useState<'off' | 'codes'>();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();

  async function start() {
    try {
      const r = await account.twoStepStart();
      setSetup({ secret: r.secret, qr: await QRCode.toDataURL(r.uri, { margin: 1, width: 200 }) });
      setMsg(undefined);
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e) });
    }
  }
  async function finish(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      const r = await account.twoStepFinish(String(new FormData(e.currentTarget).get('code') ?? '').trim());
      setCodes(r.recoveryCodes);
      setSetup(undefined);
      onChanged();
    } catch (err) {
      setMsg({ kind: 'err', text: errText(err) });
    }
  }
  async function confirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const password = String(f.get('password'));
    const code = String(f.get('code') ?? '').trim();
    try {
      if (off === 'off') {
        await account.twoStepOff(password, code);
        setMsg({ kind: 'ok', text: 'Two-step verification is off.' });
        onChanged();
      } else {
        setCodes((await account.newRecoveryCodes(password, code)).recoveryCodes);
        onChanged();
      }
      setOff(undefined);
    } catch (err) {
      setMsg({ kind: 'err', text: errText(err) });
    }
  }

  if (codes) {
    return (
      <div className="stack">
        <p className="note-ok">Two-step verification is on.</p>
        <p className="small-print">Keep these recovery codes somewhere safe (write them down or save them). Each one works once, if you ever lose your phone. You won’t see them again.</p>
        <ul className="recovery-codes">{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ul>
        <button className="btn small" onClick={() => setCodes(undefined)}>I’ve saved them</button>
      </div>
    );
  }
  if (setup) {
    return (
      <form className="stack" onSubmit={finish}>
        <p className="small-print">Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password or similar), then enter the 6-digit code it shows.</p>
        <img src={setup.qr} alt="Two-step setup QR code" width={200} height={200} />
        <p className="muted small-print">Can’t scan? Enter this key instead: <code>{setup.secret}</code></p>
        <Field id="setup-code" name="code" label="6-digit code" inputMode="numeric" autoComplete="one-time-code" required />
        {msg && <p className="form-error" role="alert">{msg.text}</p>}
        <div className="row-wrap">
          <button className="btn small">Turn on</button>
          <button type="button" className="btn small ghost" onClick={() => setSetup(undefined)}>Cancel</button>
        </div>
      </form>
    );
  }
  if (!s.enabled) {
    return (
      <div className="stack">
        <p className="muted small-print">Optional: after your password, sign-in also asks for a code from an app on your phone. It also counts toward getting verified.</p>
        <button className="btn small ghost" onClick={() => void start()}>Set up two-step verification</button>
        {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      </div>
    );
  }
  return (
    <div className="stack">
      <p className="note-ok">Two-step verification is on. {s.recoveryCodesLeft} recovery code{s.recoveryCodesLeft === 1 ? '' : 's'} left.</p>
      {off ? (
        <form className="stack" onSubmit={confirm} noValidate>
          <Field id="ts-password" name="password" label="Your password" type="password" autoComplete="current-password" required />
          <Field id="ts-code" name="code" label="Code from your app (or a recovery code)" autoComplete="one-time-code" required />
          <div className="row-wrap">
            <button className="btn small">{off === 'off' ? 'Turn off' : 'Make new codes'}</button>
            <button type="button" className="btn small ghost" onClick={() => setOff(undefined)}>Cancel</button>
          </div>
        </form>
      ) : (
        <div className="row-wrap">
          <button className="btn small ghost" onClick={() => setOff('codes')}>New recovery codes</button>
          {!isStaff && <button className="btn small ghost" onClick={() => setOff('off')}>Turn off</button>}
        </div>
      )}
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
    </div>
  );
}
